const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

export const parseArray = (value) => {
  const parsed = parseJsonValue(value);
  return Array.isArray(parsed) ? parsed : [];
};

export const isTruthyFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

export const getFiniteNumber = (value, fallback = null) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

export const parseOptionValues = (value) => {
  let parsed = parseJsonValue(value);

  if (Array.isArray(parsed)) {
    parsed = parsed.filter(
      (entry) => Array.isArray(entry) && entry.length >= 2,
    );
    parsed = Object.fromEntries(parsed);
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};

  return Object.fromEntries(
    Object.entries(parsed)
      .filter(
        ([key, optionValue]) =>
          typeof key === "string" &&
          key.trim() &&
          optionValue !== null &&
          optionValue !== undefined &&
          String(optionValue).trim(),
      )
      .map(([key, optionValue]) => [key.trim(), String(optionValue).trim()]),
  );
};

export const formatOptionValues = (value) =>
  Object.entries(parseOptionValues(value))
    .map(([key, optionValue]) => `${key}: ${optionValue}`)
    .join(", ");

export const getServerEffectivePrice = (item, fallback = 0) => {
  if (!item) return fallback;

  const explicit = getFiniteNumber(item.effective_price);
  if (explicit !== null && explicit >= 0) return explicit;

  const displayed = getFiniteNumber(item.display_sale_price);
  if (
    displayed !== null &&
    displayed >= 0 &&
    isTruthyFlag(item.display_on_sale)
  ) {
    return displayed;
  }

  const sale = getFiniteNumber(item.sale_price);
  if (isTruthyFlag(item.on_sale) && sale !== null && sale >= 0) return sale;

  const price = getFiniteNumber(item.price);
  return price !== null && price >= 0 ? price : fallback;
};

export const getOriginalPrice = (item, effectivePrice) => {
  if (!item) return null;

  const original = [
    item.display_original_price,
    item.original_price,
    item.compare_at_price,
    item.compare_price,
  ]
    .map((value) => getFiniteNumber(value))
    .find((value) => value !== null && value > effectivePrice);
  return original ?? null;
};

export const getProductVariantState = (product) => {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const hasVariants =
    isTruthyFlag(product?.has_variants) ||
    isTruthyFlag(product?.has_configured_variants) ||
    variants.length > 0;
  const activeVariants = variants.filter(
    (variant) =>
      variant &&
      (variant.is_active === undefined || isTruthyFlag(variant.is_active)),
  );
  const activeVariantStock = activeVariants.reduce(
    (total, variant) => total + Math.max(0, getFiniteNumber(variant.stock, 0)),
    0,
  );

  return { variants, hasVariants, activeVariantStock };
};

export const getProductCardPricing = (product, hasVariants) => {
  const cardPrice = hasVariants
    ? getFiniteNumber(product.min_price, getServerEffectivePrice(product))
    : getServerEffectivePrice(product);
  const displaySalePrice = getFiniteNumber(product.display_sale_price);
  const cardOriginalPrice = hasVariants
    ? getFiniteNumber(product.display_original_price)
    : getOriginalPrice(product, cardPrice);
  const comparePrice = getFiniteNumber(product.compare_price);
  const productPrice = getFiniteNumber(product.price, cardPrice);
  const legacyCompareSale =
    comparePrice !== null && comparePrice > productPrice;
  const baseHasSalePrice = hasProductSalePrice(
    product,
    displaySalePrice,
    legacyCompareSale,
  );
  const isOnSale = hasVariants
    ? isTruthyFlag(product.display_on_sale) &&
      displaySalePrice !== null &&
      Math.round(displaySalePrice * 100) === Math.round(cardPrice * 100) &&
      cardOriginalPrice !== null &&
      cardOriginalPrice > cardPrice
    : baseHasSalePrice &&
      cardOriginalPrice !== null &&
      cardOriginalPrice > cardPrice;
  const discount =
    isOnSale && cardOriginalPrice
      ? Math.round(((cardOriginalPrice - cardPrice) / cardOriginalPrice) * 100)
      : 0;

  return { cardPrice, cardOriginalPrice, isOnSale, discount };
};

const getActiveVariants = (product) =>
  parseArray(product?.variants).filter(
    (variant) =>
      variant &&
      (variant.is_active === undefined || isTruthyFlag(variant.is_active)),
  );

const buildOptionGroups = (variants) => {
  const groups = {};
  variants.forEach((variant) => {
    Object.entries(parseOptionValues(variant.option_values)).forEach(
      ([key, value]) => {
        if (!groups[key]) groups[key] = [];
        if (!groups[key].includes(value)) groups[key].push(value);
      },
    );
  });
  return groups;
};

const matchesVariantSelection = (variant, group, value, selectedOptions) => {
  const options = parseOptionValues(variant.option_values);
  if (options[group] !== value || getFiniteNumber(variant.stock, 0) <= 0) {
    return false;
  }
  return Object.entries(selectedOptions).every(
    ([otherGroup, otherValue]) =>
      otherGroup === group || options[otherGroup] === otherValue,
  );
};

export const getVariantSelectionData = (product, selectedOptions) => {
  const variants = getActiveVariants(product);
  const hasConfiguredVariants =
    isTruthyFlag(product.has_variants) ||
    isTruthyFlag(product.has_configured_variants) ||
    getFiniteNumber(product.variant_count, 0) > 0;
  const hasVariants = hasConfiguredVariants || variants.length > 0;
  const optionGroups = buildOptionGroups(variants);
  const groupNames = Object.keys(optionGroups);
  const availableValues = (group) =>
    optionGroups[group]?.filter((value) =>
      variants.some((variant) =>
        matchesVariantSelection(variant, group, value, selectedOptions),
      ),
    ) || [];
  const allSelected =
    groupNames.length > 0 &&
    groupNames.every((group) => selectedOptions[group]);
  const selectedVariant =
    hasVariants && allSelected
      ? variants.find((variant) => {
          const options = parseOptionValues(variant.option_values);
          return groupNames.every(
            (group) => options[group] === selectedOptions[group],
          );
        }) || null
      : null;
  const variantInStock =
    selectedVariant !== null && getFiniteNumber(selectedVariant.stock, 0) > 0;

  return {
    variants,
    hasVariants,
    optionGroups,
    groupNames,
    availableValues,
    allSelected,
    selectedVariant,
    variantInStock,
  };
};

const getProductSaleDisplay = (
  product,
  effectivePrice,
  hasVariants,
  selectedVariant,
) => {
  const selectedOriginalPrice = selectedVariant
    ? getOriginalPrice(selectedVariant, effectivePrice)
    : null;
  const displaySalePrice = getFiniteNumber(product.display_sale_price);
  const displayOriginalPrice = getFiniteNumber(product.display_original_price);
  const cheapestVariantIsOnSale =
    hasVariants &&
    isTruthyFlag(product.display_on_sale) &&
    displaySalePrice !== null &&
    Math.round(displaySalePrice * 100) === Math.round(effectivePrice * 100) &&
    displayOriginalPrice !== null &&
    displayOriginalPrice > effectivePrice;
  const baseOriginalPrice = getOriginalPrice(product, effectivePrice);
  const comparePrice = getFiniteNumber(product.compare_price);
  const legacyCompareSale =
    comparePrice !== null &&
    comparePrice > getFiniteNumber(product.price, effectivePrice);
  const baseHasSalePrice = hasProductSalePrice(
    product,
    displaySalePrice,
    legacyCompareSale,
  );
  const isSaleActive = hasVariants
    ? selectedVariant
      ? isTruthyFlag(selectedVariant.on_sale) && selectedOriginalPrice !== null
      : cheapestVariantIsOnSale
    : baseHasSalePrice &&
      baseOriginalPrice !== null &&
      baseOriginalPrice > effectivePrice;
  const originalPrice = hasVariants
    ? selectedVariant
      ? selectedOriginalPrice
      : cheapestVariantIsOnSale
        ? displayOriginalPrice
        : null
    : isSaleActive && baseOriginalPrice > effectivePrice
      ? baseOriginalPrice
      : null;
  const discount =
    isSaleActive && originalPrice && Number(originalPrice) > effectivePrice
      ? Math.round(
          ((Number(originalPrice) - effectivePrice) / Number(originalPrice)) *
            100,
        )
      : 0;

  return { isSaleActive, originalPrice, discount };
};

const getProductStockDisplay = (
  product,
  variants,
  hasVariants,
  selectedVariant,
  allSelected,
  variantInStock,
) => {
  const variantStockTotal = variants.reduce(
    (sum, variant) => sum + Math.max(0, getFiniteNumber(variant.stock, 0)),
    0,
  );
  const displayStock = hasVariants
    ? selectedVariant
      ? getFiniteNumber(selectedVariant.stock, 0)
      : variantStockTotal
    : getFiniteNumber(product.stock);
  const hasStock = displayStock !== null;
  const inStock = hasStock && displayStock > 0;
  const variantShipping =
    hasVariants && selectedVariant
      ? selectedVariant.shipping_price !== null &&
        selectedVariant.shipping_price !== undefined
        ? getFiniteNumber(selectedVariant.shipping_price, 0)
        : getFiniteNumber(product.shipping_price, 0)
      : null;
  const canAddToCart = !hasVariants
    ? inStock
    : allSelected && !!selectedVariant && variantInStock && inStock;

  return {
    displayStock,
    hasStock,
    inStock,
    maxQty: hasStock && displayStock > 0 ? displayStock : 99,
    variantShipping,
    canAddToCart,
  };
};

export const getProductDisplayData = (
  product,
  variants,
  hasVariants,
  selectedVariant,
  allSelected,
  variantInStock,
) => {
  const baseEffectivePrice = getServerEffectivePrice(product);
  const effectivePrice = hasVariants
    ? selectedVariant
      ? getServerEffectivePrice(selectedVariant)
      : getFiniteNumber(product.min_price, baseEffectivePrice)
    : baseEffectivePrice;
  const sale = getProductSaleDisplay(
    product,
    effectivePrice,
    hasVariants,
    selectedVariant,
  );
  const stock = getProductStockDisplay(
    product,
    variants,
    hasVariants,
    selectedVariant,
    allSelected,
    variantInStock,
  );

  return {
    effectivePrice,
    displayedPrice: effectivePrice,
    displayedOriginalPrice: sale.isSaleActive ? sale.originalPrice : null,
    discount: sale.discount,
    ...stock,
  };
};

const hasProductSalePrice = (product, displaySalePrice, legacyCompareSale) => {
  if (
    product.effective_price !== undefined ||
    product.display_on_sale !== undefined
  ) {
    return isTruthyFlag(product.display_on_sale) && displaySalePrice !== null;
  }
  return (
    (isTruthyFlag(product.on_sale) &&
      getFiniteNumber(product.sale_price) !== null) ||
    legacyCompareSale
  );
};
